import { getQueryString } from '@/common/url';
import { Country, MINIMIZE_TIME } from '@/common/config';
import { emitAppFloatButton, emitFloatButton, emitOpenArticle, emitOpenMyRequest, EventBus } from '@/common/eventBus';
import { LoginResult, SelectedEntity } from './types';
import Vue from 'vue';
import { store } from '@/store';
import { Conversation } from '@/views/Chatroom/active-conversations/type';
import { event } from 'vue-gtag';
import { GA_EVENT_NAME_ENUM } from './gaHelper';
import { DEEP_LINK_URL_POSTFIX, isFirstVersionArticleAction, PLEASED_ARTICLE_APP_ACTION, PLEASED_ARTICLE_DEEP_LINKS, PleasedArticleAction, SHORTCUT_ARTICLE_ACTIONS } from './bot/flow-frameworks/ChatMessageActions.static';
import * as env from '@/common/environment';

// Document: https://opennetltd.atlassian.net/wiki/spaces/SPOR/pages/151158830/SD+Level+2+FE+-+Interface+Spec
interface ParentEventData {
  appName: string;
  eventType: string;
  data: any;
 }

const SPORTY_DESK = 'SPORTY_DESK';
const SPORTY_BET = 'SPORTY_BET';

// TODO: [Sportybet]: 1. 'INIT' shouldn't pass token from new client 2. Is it proper pleased-chat-bot get LOGIN for username and password ?
const availableAndroidSendEvents = ['APP_HAS_VOICE_PERMISSION', 'APP_GO_SETTING'];
const availableNativeSendEvents = ['NEW_MESSAGE', 'CLEAR_FLOAT'];
const availableSendEvents = ['INIT', 'LOGIN', 'MINIMIZE', 'LOGIN_POP', 'CLOSE', 'GO_TO_ACTION', ...availableAndroidSendEvents, availableNativeSendEvents];

const availableAndroidReceiveEvents = ['APP_HAS_VOICE_PERMISSION'];
const availableBrowserReceiveEvents = ['OPEN'];
const availableReceiveEvents = ['INIT', 'LOGIN', 'LOGIN_POP', 'GO_LIVE_CHAT', ...availableAndroidReceiveEvents, ...availableBrowserReceiveEvents];
// GO_LIVE_CHAT -> deprecated
// Android ONLY: 'APP_HAS_VOICE_PERMISSION', 'APP_GO_SETTING', 'APP_HAS_VOICE_PERMISSION'
// Android / iOS ONLY: 'NEW_MESSAGE', 'CLEAR_FLOAT'

export interface ParentEvDataInitRes {
  avatarImgUrl: string;
  chatStatus: string;
  country: Country;
  phone: string;
  token: string;
  userId: string;
  provider?: string;
  open: boolean;
}
export interface ParentEvDataOpenRes {
  open: boolean;
  selectedEntity: SelectedEntity | null;
}

export interface ParentEvDataLoginRes {
  isLogin: boolean;
  token: string;
}

export interface ParentEvDataVoicePermission {
  permission: boolean;
}

export const platform = getQueryString('platform') || 'wap'; // wap, web, app
export const version = getQueryString('version') || 0; // For old implementation on Sportybet side (float button was handled on Sportybet side)

export type AllPlatforms = 'ios' | 'app' | 'web' | 'wap';

const BridgeEventsBus = new Vue();
/**
 * SPEC:
 * {
 *  appName: 'SPORTY_DESK',
 *  eventType: string
 * }
 *
 *  eventType: 'LOGIN'
 *  eventType: 'MINIMIZE'
 */
export const sendToParent = (
  eventType: string,
  data: any = {},
) => {
  if (!availableSendEvents.includes(eventType)) return;
  BridgeEventsBus.$emit('SENDING_EVENT_TO_PARENT', { eventType, data });
  switch (platform) {
    case 'wap':
    case 'web':
      window.parent.postMessage({
        appName: SPORTY_DESK,
        eventType,
        data,
      }, '*');
      break;
    case 'app':
      const androidResult = JSON.stringify({
        appName: SPORTY_DESK,
        eventType,
        data,
      });
      const androidInterface = window.pleased_bridge || window.app_receiver;
      androidInterface?.sendCmd(androidResult);
      break;
    case 'ios':
      const iosResult = JSON.stringify({
        appName: SPORTY_DESK,
        eventType,
        data,
      });
      // IMPORTANT: app_recevier is correct name here, do no change it
      const iosInterface = window.webkit && window.webkit.messageHandlers && (window.webkit.messageHandlers.pleased_bridge || window.webkit.messageHandlers?.app_recevier);
      iosInterface?.postMessage(iosResult);
      break;
    default:
  }
};

/**
 * SPEC:
 * {
 *   appName: 'SPORTY_BET',
 *   eventType: string,
 *   data: any
 * }
 *
 *  eventType: 'INIT',
 *  data: {
 *    isLogin: boolean,
 *    token: string || null,
 *    countryCode: string
 *  }
 *
 *  eventType: 'LOGIN',
 *  data: {
 *    token: string
 *    errCode: 0
 *  }
 */

// For App
export const registerEventFromApp = () => {
  if ((platform !== 'app' && platform !== 'ios') || (<any>window).sportydesk_receiver) return;
  (<any>window).sportydesk_receiver = {};
  (<any>window).sportydesk_receiver.sendCmd = function (info: string) {
    const result = (JSON.parse(info) as ParentEventData);
    if (
      result.appName !== SPORTY_BET ||
      !availableReceiveEvents.includes(result.eventType)
    ) return;

    EventBus.$emit(`Bridge_${result.eventType}`, info);
  };
};

export const listenFromParent = <T extends any = any>(
  eventType: string,
  callback: (data: T) => void = () => {},
) => {
  let runnable: Function | null = callback;
  switch (platform) {
    case 'wap':
    case 'web':
      const listener = function (msg: MessageEvent) {
        const msgInfo = <ParentEventData>msg.data;
        if (
          msgInfo.appName !== SPORTY_BET ||
          !availableReceiveEvents.includes(msgInfo.eventType) ||
          msgInfo.eventType !== eventType
        ) return;
        runnable?.(msgInfo.data);
      };
      window.addEventListener('message', listener);
      return () => { window.removeEventListener('message', listener); runnable = null; };
    case 'app':
    case 'ios':
      const event = `Bridge_${eventType}`;
      const eventListener = (info: any) => {
        const jsonInfo = JSON.parse(info);
        runnable?.(jsonInfo.data);
      };
      EventBus.$on(event, eventListener);
      return () => { EventBus.$off(event, eventListener); };
    default:
  }
  return () => { runnable = null; };
};
/**
 * Bridge service. TODO: Migrate all options here.
 */
class BridgeService {
  isMinimized = false;
  platform = platform;

  userRedirected = false;
  eventsStack: any[] = [];

  constructor () {
    // Setup listeners.
    BridgeEventsBus.$on('SENDING_EVENT_TO_PARENT', ({ eventType, data }: { eventType: string, data: any }) => {
      if (eventType === 'MINIMIZE') this.isMinimized = !data?.open;
    });

    listenFromParent('INIT', (data) => this.eventsStack.push(data));
    listenFromParent('OPEN', (data) => this.eventsStack.push(data));
  }

  login (data: {username: string, password: string}) {
    return new Promise<LoginResult>(resolve => {
      sendToParent('LOGIN', data);
      const removeListener = listenFromParent('LOGIN', (...args: [LoginResult]) => {
        removeListener();
        resolve(...args);
      });
    });
  }

  onLoginFromPopup (onComplete: (res: LoginResult) => unknown) {
    return listenFromParent('LOGIN_POP', onComplete);
  }

  onLoginSuccess (onComplete: (res: LoginResult) => unknown) {
    return listenFromParent('LOGIN', onComplete);
  }

  minimize ({ avoidNoop = false }: { avoidNoop?: boolean } = {}) {
    const { platform, isMinimized } = this;

    // We want to redirect user to last event page when user minimize the chat & opens later
    this.userRedirected = false;

    if (isMinimized && avoidNoop) return;
    if (platform === 'wap' || platform === 'web') {
      emitFloatButton(true);
      store.commit('chat/updateNewMessage', false);
    } else {
      emitAppFloatButton(true);
    }

    event(GA_EVENT_NAME_ENUM.MINIMIZE_CHATBOT);
    sendToParent('MINIMIZE', { open: false, period: MINIMIZE_TIME });
  }

  maximize ({ avoidNoop = false }: { avoidNoop?: boolean } = {}) {
    const { platform, isMinimized } = this;
    if (!isMinimized && avoidNoop) return;
    if (platform === 'wap' || platform === 'web') {
      emitFloatButton(false);
    } else {
      emitAppFloatButton(false);
    }

    sendToParent('MINIMIZE', { open: true, period: MINIMIZE_TIME });
  }

  toggleCover () {
    return this.isMinimized ? this.maximize() : this.minimize();
  }

  goToAction (action?: PleasedArticleAction | null) {
    const { platform } = this;

    if (!action) return;

    const isFirstVersionAction = isFirstVersionArticleAction(action);

    if (platform === 'wap' || platform === 'web' || isFirstVersionAction) {
      sendToParent('GO_TO_ACTION', { action });
    } else {
      const country = env.get('country');
      const { deepLinkUrl, deepLinkShortcut } = env.get('app') || { deepLinkUrl: '', deepLinkShortcut: '' };

      const isShortcutLink = SHORTCUT_ARTICLE_ACTIONS.includes(action);
      const deepLink = isShortcutLink ? deepLinkShortcut : deepLinkUrl;
      const deepLinkPostfix = isShortcutLink ? '' : DEEP_LINK_URL_POSTFIX;

      if (!deepLink || !country) {
        console.error('No deep link or country found for the action');
        return;
      }

      const pageUrl = `${deepLink}/${country}/${deepLinkPostfix}/${PLEASED_ARTICLE_DEEP_LINKS[action]}`;

      console.log('GO_TO_ACTION app', { action: PLEASED_ARTICLE_APP_ACTION, page: pageUrl });
      sendToParent('GO_TO_ACTION', { action: PLEASED_ARTICLE_APP_ACTION, page: pageUrl });
    }
  }

  openActiveConversation (ticket: Conversation) {
    BridgeEventsBus.$emit('OPEN_ACTIVE_CONVERSATION', ticket);
  }

  onOpenActiveConversation (handler: (ticket: Conversation) => void) {
    BridgeEventsBus.$on('OPEN_ACTIVE_CONVERSATION', handler);
    return () => BridgeEventsBus.$off('OPEN_ACTIVE_CONVERSATION', handler);
  }

  redirectToLastEventPage () {
    if (!this.eventsStack.length) return;

    const lastEvent = this.eventsStack[this.eventsStack.length - 1];

    if (!lastEvent || this.userRedirected) return;

    this.userRedirected = true;

    if (lastEvent.myRequest) {
      emitOpenMyRequest(true);
    } else if (lastEvent.articleId) {
      emitOpenArticle({ isOpen: true, articleId: lastEvent.articleId, fetchArticle: true });
    }
  }
}

export const Bridge = new BridgeService();
